import { useCallback, useMemo, useState } from 'react';
import { Box, HStack, VStack, Menu, Pressable, ChevronDownIcon, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Text } from '@pimm/base';
import { CleaningScheduleTypeEnum } from '@pimm/services/lib/sms-workforce/types';
import { CalendarPlus, CopyCheck, Info, Sparkles } from 'lucide-react';

type PopoverScheduleMenuProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  copyEligibleCount: number;
  bulkEligibleCount: number;
  onEnterCopyLastWeekMode: () => void;
  onEnterBulkMode: () => void;
  /** Compact icon-only trigger (used in the section header tiles on the main page). */
  compact?: boolean;
  /** Schedule type drives the "Copy last week's" vs "Copy last month's" wording. */
  scheduleType?: CleaningScheduleTypeEnum;
};

export const PopoverScheduleMenu = ({
  isOpen,
  onOpen,
  onClose,
  copyEligibleCount,
  bulkEligibleCount,
  onEnterCopyLastWeekMode,
  onEnterBulkMode,
  compact = false,
  scheduleType,
}: PopoverScheduleMenuProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [expandedItem, setExpandedItem] = useState<'copy' | 'bulk'>();
  const hasEligible = copyEligibleCount > 0 || bulkEligibleCount > 0;

  const isMonthly = scheduleType === CleaningScheduleTypeEnum.Monthly;
  const copyTitle = isMonthly ? t('cleaning_task:copy_last_month_schedule') : t('cleaning_task:copy_last_week_schedule');
  const copyDescription = isMonthly
    ? t('cleaning_task:apply_last_month_times')
    : t('cleaning_task:apply_last_week_times');

  const menuItems = useMemo(
    () => [
      {
        key: 'copy' as const,
        renderIcon: color => <Sparkles size={16} color={color} />,
        title: copyTitle,
        description: copyDescription,
        isEligible: copyEligibleCount > 0,
        onAction: onEnterCopyLastWeekMode,
        ineligibleReason: {
          title: t('cleaning_task:all_tasks_are_complete'),
          body: t('cleaning_task:every_task_completed_or_na'),
        },
      },
      {
        key: 'bulk' as const,
        renderIcon: color => <CopyCheck size={16} color={color} />,
        title: 'cleaning_task:bulk_assign_time',
        description: 'cleaning_task:select_tasks_apply_time',
        isEligible: bulkEligibleCount > 0,
        onAction: onEnterBulkMode,
        ineligibleReason: {
          title: t('cleaning_task:bulk_assign_time_to_tasks'),
          body: t('cleaning_task:bulk_schedule_only_applies_to_tasks_without_employee'),
        },
      },
    ],
    [copyEligibleCount, bulkEligibleCount, copyTitle, copyDescription],
  );

  const handleClose = useCallback(() => {
    setExpandedItem(undefined);
    onClose();
  }, [onClose]);

  return (
    <Menu
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      placement="bottom right"
      width={270}
      p={0}
      mt={3}
      borderRadius="xl"
      overflow="hidden"
      trigger={triggerProps =>
        compact ? (
          <Pressable {...triggerProps}>
            <HStack
              h="30px"
              px={2}
              space={1.5}
              alignItems="center"
              bg={hasEligible ? 'primary.500' : 'gray.100'}
              borderRadius={6}
            >
              <CalendarPlus size={16} color={hasEligible ? colors.gray[900] : colors.gray[500]} />
              <Text size="sm" fontWeight={600} color={hasEligible ? 'gray.900' : 'gray.500'}>
                {t('cleaning_task:schedule')}
              </Text>
              <ChevronDownIcon size={2} color={hasEligible ? 'gray.900' : 'gray.500'} />
            </HStack>
          </Pressable>
        ) : (
          <Pressable {...triggerProps}>
            <HStack
              px={3}
              py={2}
              space={2}
              alignItems="center"
              bg={hasEligible ? 'primary.400' : 'gray.50'}
              borderRadius="md"
              borderWidth={1}
              borderColor={hasEligible ? 'primary.400' : 'gray.200'}
              style={
                isOpen
                  ? {
                      shadowColor: colors.gray[500],
                      shadowOpacity: 0.8,
                      shadowRadius: 8,
                      elevation: 8,
                    }
                  : undefined
              }
            >
              <CalendarPlus size={20} color={hasEligible ? colors.gray[900] : colors.gray[500]} />
              <Text size="md" fontWeight={600} color={hasEligible ? colors.gray[900] : colors.gray[500]}>
                {t('cleaning_task:schedule')}
              </Text>
              <ChevronDownIcon size={2} color={hasEligible ? colors.gray[900] : colors.gray[500]} />
            </HStack>
          </Pressable>
        )
      }
    >
      {menuItems.map(({ key, renderIcon, title, description, isEligible, onAction, ineligibleReason }, index) => {
        const isExpanded = expandedItem === key;
        const isLast = index === menuItems.length - 1;
        const showDivider = !isLast;

        return (
          <Box
            key={key}
            flex={1}
            py={isEligible ? 0 : 2}
            background="white"
            borderBottomWidth={showDivider ? 1 : 0}
            borderColor={showDivider ? 'gray.100' : 'transparent'}
          >
            {isEligible ? (
              <Menu.Item
                onPress={onAction}
                _hover={{ bg: 'transparent' }}
                _pressed={{ bg: 'transparent' }}
                _focus={{ bg: 'transparent' }}
                px={3}
                py={2}
              >
                <VStack w="full">
                  <HStack space={3} alignItems="center" py={1}>
                    <Box
                      w={7}
                      h={7}
                      rounded="md"
                      borderWidth={1}
                      alignItems="center"
                      justifyContent="center"
                      bg="amber.50"
                      borderColor="amber.200"
                    >
                      {renderIcon(colors.primary[700])}
                    </Box>
                    <VStack flex={1}>
                      <Text size="md" fontWeight={600} color="gray.900" lineHeight="md">
                        {t(title)}
                      </Text>
                      <Text size="sm" color="gray.700" lineHeight="sm">
                        {t(description)}
                      </Text>
                    </VStack>
                  </HStack>
                </VStack>
              </Menu.Item>
            ) : (
              <Pressable onPress={() => setExpandedItem(prev => (prev === key ? undefined : key))} px={1} py={1}>
                <VStack w="full">
                  <HStack space={3} alignItems="center" py={1} px={2}>
                    <Box
                      w={7}
                      h={7}
                      rounded="md"
                      borderWidth={1}
                      alignItems="center"
                      justifyContent="center"
                      bg="gray.100"
                      borderColor="gray.200"
                    >
                      {renderIcon(colors.gray[500])}
                    </Box>
                    <VStack flex={1}>
                      <Text size="md" fontWeight={600} color="gray.500">
                        {t(title)}
                      </Text>
                      <Text size="sm" color="gray.500">
                        {isExpanded ? t('cleaning_task:hide_details') : t('cleaning_task:see_why_unavailable')}
                      </Text>
                    </VStack>
                    <ChevronDownIcon size="3" color="gray.400" style={{ transform: [{ rotate: isExpanded ? '180deg' : '0deg' }] }} />
                  </HStack>
                </VStack>
              </Pressable>
            )}

            {isExpanded && !isEligible && (
              <Box bg="primary.50" borderRadius="md" p={3} mx={2} mt={2} borderWidth={1} borderColor="primary.100">
                <HStack space={2} alignItems="flex-start">
                  <Info size={13} color={colors.primary[700]} />
                  <VStack flex={1} space={1}>
                    <Text size="sm" fontWeight={600} color="primary.700">
                      {ineligibleReason.title}
                    </Text>
                    <Text size="sm" color="gray.700" lineHeight="sm">
                      {ineligibleReason.body}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            )}
          </Box>
        );
      })}
    </Menu>
  );
};

export default PopoverScheduleMenu;
